// Wizard draft persistence in localStorage with TTL (D-25, INT-07, T-DRAFT_LEAK).
//
// Anonymity rationale: localStorage is browser-only. The draft is NEVER synced
// to the server. We store the wizard fields (categoria/descricao/etc) so a
// denunciante that closes the tab mid-flow can resume — but we expire the
// draft after 24h and clear it the moment Step 5 is reached.
//
// We deliberately DROP File objects from the persisted draft because:
//   1. localStorage cannot serialize File contents reliably across browsers.
//   2. Persisting evidence in plaintext on disk would be a regression vs the
//      "encrypt before anything touches storage" rule.
// Files must be re-attached on resume.
//
// P10 (ADR-0018) — pela MESMA regra do item 2, os campos de identificação
// voluntária (nome/cpf/contato) também NÃO são persistidos. Eles nasceram depois
// deste arquivo e entravam por omissão no `...rest`: nome + CPF + contato em
// texto puro no disco por 24h, em máquina corporativa compartilhada, exatamente
// o dado que o resto do sistema cifra no navegador. O bloco de identidade só
// existe cifrado (lib/identidade-block.ts), e agora o rascunho respeita isso.
//
// O BOOLEANO `identificacaoVoluntaria` PERMANECE: apagar a intenção também é
// alterar a decisão da pessoa. Ao retomar, o painel reabre — vazio — e a copy
// explica por que os campos voltaram em branco (step4.identidade_nao_salvo).

import type { WizardData } from '../pages/IntakeWizard';

export const DRAFT_STORAGE_KEY = 'aions:intake:draft:v1';
export const DRAFT_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const DRAFT_VERSION = 'v1';

/** P10 — campos de identidade, nunca persistidos. Fonte única das duas pontas
 *  (sanitização na escrita E na leitura). */
const IDENTIDADE_VAZIA = { nome: '', cpf: '', contato: '' } as const;

interface PersistedDraft {
  readonly version: typeof DRAFT_VERSION;
  readonly savedAt: number;
  readonly data: Omit<WizardData, 'files' | keyof typeof IDENTIDADE_VAZIA>;
}

/** Persists the wizard state. Files and P10 identity fields are dropped (see header). */
export function saveDraft(state: WizardData): void {
  if (typeof localStorage === 'undefined') return;
  const { files: _files, nome: _nome, cpf: _cpf, contato: _contato, ...rest } = state;
  void _files;
  void _nome;
  void _cpf;
  void _contato;
  const payload: PersistedDraft = {
    version: DRAFT_VERSION,
    savedAt: Date.now(),
    data: rest,
  };
  try {
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Quota exceeded / storage disabled — degrade silently. The wizard
    // continues to work without persistence.
  }
}

/** Loads + validates the draft. Returns null when missing/expired/version-mismatched. */
export function loadDraft(): WizardData | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!isPersistedDraft(parsed)) return null;
  if (parsed.version !== DRAFT_VERSION) return null;
  if (Date.now() - parsed.savedAt > DRAFT_TTL_MS) return null;

  return {
    ...parsed.data,
    files: [], // Always reset on load — see header rationale.
    // P10 — sanitiza também na LEITURA, e depois do spread de propósito: um
    // rascunho escrito pela versão que vazava pode estar no disco agora, dentro
    // das 24h de TTL. Limpar só na escrita deixaria esse rascunho legado
    // reidratando a identidade na tela.
    ...IDENTIDADE_VAZIA,
  };
}

/** Clears the draft. Idempotent. */
export function clearDraft(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

function isPersistedDraft(value: unknown): value is PersistedDraft {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  if (typeof obj.savedAt !== 'number') return false;
  if (typeof obj.version !== 'string') return false;
  if (typeof obj.data !== 'object' || obj.data === null) return false;
  return true;
}
